import { Timer, type WebGPURenderer } from 'three/webgpu';
import { AudioEngine } from './audio/engine.js';
import { MorphField, type MorphFieldOptions } from './morph/field.js';
import { Atmosphere } from './render/background.js';
import { PostStack } from './render/post.js';
import { createRenderer } from './render/renderer.js';
import { type LoadedCharacter, tryLoadCharacter } from './scene/loader.js';
import { StarField } from './scene/particles.js';
import { Stage } from './scene/stage.js';
import { bindControls, bindPulse, hideOverlay, setStatus } from './ui/controls.js';

/** How one page of the visualiser differs from another. */
export interface VisualiserOptions {
  /** The forms this page cycles through. Defaults to the full registry. */
  readonly shapes?: MorphFieldOptions['shapes'];
  /** How hard the field swells with the music. Defaults to 1. */
  readonly pulse?: number;
  /** Rigged GLB to try adding to the scene, or null for none. */
  readonly characterUrl?: string | null;
}

/** A fresh seed per page load, so no two sessions walk the forms the same way. */
function randomSeed(): number {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return (bytes[0] ?? 1) >>> 1;
}

class VisualiserApp {
  private readonly renderer: WebGPURenderer;
  private readonly stage: Stage;
  private readonly atmosphere = new Atmosphere();
  private readonly starField = new StarField();
  private readonly field: MorphField;
  private readonly post: PostStack;
  private readonly audio = new AudioEngine();
  private readonly timer = new Timer();
  private readonly characterUrl: string | null;
  private character: LoadedCharacter | null = null;
  public warmed = 0;

  constructor(renderer: WebGPURenderer, options: VisualiserOptions) {
    this.renderer = renderer;
    this.characterUrl = options.characterUrl ?? null;
    this.stage = new Stage(globalThis.innerWidth / globalThis.innerHeight);
    this.stage.scene.backgroundNode = this.atmosphere.node;
    this.stage.scene.add(this.starField.group);
    // A different journey through the forms on every page load.
    const requested = Number(new URLSearchParams(globalThis.location.search).get('seed') ?? '0');
    const seed = Number.isFinite(requested) && requested > 0 ? requested : randomSeed();
    this.field = new MorphField(seed, { shapes: options.shapes, pulse: options.pulse });
    this.stage.scene.add(this.field.mesh);
    this.post = new PostStack(renderer, this.stage.scene, this.stage.camera);
  }

  public start(): void {
    this.bindUi();
    globalThis.addEventListener('resize', () => {
      this.handleResize();
    });
    void this.renderer.setAnimationLoop(() => {
      this.tick();
    });
    void this.upgradeCharacter();
  }

  /** Add a rigged GLB character to the scene when one exists in /public/models. */
  private async upgradeCharacter(): Promise<void> {
    if (this.characterUrl === null) {
      return;
    }
    const character = await tryLoadCharacter(this.characterUrl);
    if (character === null) {
      return;
    }
    this.character = character;
    this.stage.scene.add(character.root);
  }

  private bindUi(): void {
    bindControls({
      onFile: (file) => {
        void this.playFile(file);
      },
      onMicrophone: () => {
        void this.useMicrophone();
      },
    });
    bindPulse((value) => {
      this.field.setPulse(value);
    });
  }

  private async playFile(file: File): Promise<void> {
    setStatus(`Loading ${file.name}…`);
    try {
      await this.audio.playFile(file);
      hideOverlay();
      setStatus(`Playing: ${this.audio.currentTrackName}`);
    } catch (error) {
      setStatus('Could not play that file — is it a valid audio format?');
      console.error(error);
    }
  }

  private async useMicrophone(): Promise<void> {
    try {
      await this.audio.useMicrophone();
      hideOverlay();
      setStatus('Listening to microphone');
    } catch (error) {
      setStatus('Microphone access was denied.');
      console.error(error);
    }
  }

  private handleResize(): void {
    const aspect = globalThis.innerWidth / globalThis.innerHeight;
    this.stage.resize(aspect);
    this.atmosphere.setAspect(aspect);
    this.renderer.setSize(globalThis.innerWidth, globalThis.innerHeight);
  }

  /** Debug: advance the whole simulation without drawing, to reach a settled state. */
  public warmUp(seconds: number): void {
    const step = 1 / 60;
    for (let t = 0; t < seconds; t += step) {
      this.advance(step, t);
    }
  }

  private tick(): void {
    this.timer.update();
    const deltaSeconds = Math.min(0.1, this.timer.getDelta());
    const elapsedSeconds = this.timer.getElapsed();
    this.advance(deltaSeconds, elapsedSeconds + this.warmed);
    this.post.render();
  }

  private advance(deltaSeconds: number, elapsedSeconds: number): void {
    const frame = this.audio.sample();

    this.field.update(frame, deltaSeconds);
    this.atmosphere.update(frame, elapsedSeconds, deltaSeconds);
    this.atmosphere.tint(this.field.ambient, deltaSeconds);
    this.starField.update(frame, deltaSeconds);
    this.stage.update(frame, elapsedSeconds, deltaSeconds, {
      focus: this.field.focus,
      move: this.field.segment.camera,
      framing: this.field.framing,
      visit: this.field.visit,
      blending: this.field.blending,
    });
    this.post.update(frame, deltaSeconds);

    if (this.character !== null) {
      // Rigged characters dance faster when the music gets louder.
      this.character.mixer.update(deltaSeconds * (0.6 + frame.level * 1.2));
    }
  }
}

/** Boot one page of the visualiser onto the `#stage` canvas. */
export async function startVisualiser(options: VisualiserOptions = {}): Promise<void> {
  const canvas = document.querySelector('#stage');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new TypeError('Canvas #stage is missing from the page');
  }
  const renderer = await createRenderer(canvas);
  const app = new VisualiserApp(renderer, options);
  const warm = Number(new URLSearchParams(globalThis.location.search).get('warm') ?? '0');
  if (warm > 0) {
    app.warmed = warm;
    app.warmUp(warm);
  }
  app.start();
}
